import React, { useState, useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { LineChart, Line } from 'recharts';
import YearSelector from './YearSelector';

// Example data for UnemploymentLineChart
const unemploymentData = [
  { month: 'Jan', rate: 3.8 },
  { month: 'Feb', rate: 3.7 },
  { month: 'Mar', rate: 3.6 },
  { month: 'Apr', rate: 3.5 },
  { month: 'May', rate: 3.6 },
  { month: 'Jun', rate: 3.7 },
];

export const UnemploymentLineChart = () => (
  <ResponsiveContainer width="100%" height={300}>
    <LineChart data={unemploymentData}>
      <CartesianGrid strokeDasharray="3 3" />
      <XAxis dataKey="month" />
      <YAxis />
      <Tooltip />
      <Legend />
      <Line
        type="monotone"
        dataKey="rate"
        stroke="#82ca9d"
        name="Unemployment Rate (%)"
      />
    </LineChart>
  </ResponsiveContainer>
);

/**
 * GDPBarChart
 * Transforms the raw data (quarter, state, gdp) into grouped data
 * with "US" and "CT" fields for each quarter.
 */
export const GDPBarChart = ({ data = [] }) => {
  const [timeFrame, setTimeFrame] = useState(5);

  // Convert raw data into { quarter, US, CT }
  const barData = useMemo(() => {
    const grouped = {};
    data.forEach(item => {
      if (!grouped[item.quarter]) {
        grouped[item.quarter] = { quarter: item.quarter, US: 0, CT: 0 };
      }
      if (item.state === 'United States') {
        grouped[item.quarter].US = item.gdp;
      } else if (item.state === 'Connecticut') {
        grouped[item.quarter].CT = item.gdp;
      }
    });
    return Object.values(grouped).sort((a, b) => (a.quarter > b.quarter ? 1 : -1));
  }, [data]);

  const filteredData = barData.slice(-timeFrame);

  return (
    <div>
      <YearSelector timeFrame={timeFrame} setTimeFrame={setTimeFrame} />
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={filteredData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="quarter" />
          <YAxis />
          <Tooltip />
          <Legend />
          <Bar dataKey="US" fill="#8884d8" name="US GDP (Billion $)" />
          <Bar dataKey="CT" fill="#82ca9d" name="CT GDP (Billion $)" />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

/**
 * GDPIndexChart
 * Uses the raw data (quarter, state, gdp) to compute an indexed GDP value
 * for both US and CT.
 */
export const GDPIndexChart = ({ data }) => {
  const [timeFrame, setTimeFrame] = useState(5);

  // Calculate indexed GDP values
  const indexedData = useMemo(() => {
    // Check if data is a valid array
    if (!data || !Array.isArray(data) || data.length === 0) return [];
    
    const ctData = data.filter(item => item && item.state === 'Connecticut');
    const usData = data.filter(item => item && item.state === 'United States');
    if (ctData.length === 0 || usData.length === 0) return [];

    // Calculate the starting point for the timeframe
    const baseIndex = Math.max(ctData.length, usData.length) - (timeFrame * 4 + 1);
    if (baseIndex < 0) return []; // Not enough data for timeframe

    const baseCT = ctData[baseIndex]?.gdp || 1;
    const baseUS = usData[baseIndex]?.gdp || 1;

    // Pair the data and scale each quarter to its base
    return ctData.slice(baseIndex).map((ctItem, idx) => {
      const usItem = usData[baseIndex + idx];
      return {
        quarter: ctItem.quarter,
        US: usItem?.gdp
          ? Number(((usItem.gdp / baseUS) * 100).toFixed(1))
          : 0,
        CT: Number(((ctItem.gdp / baseCT) * 100).toFixed(1)),
      };
    });
  }, [data, timeFrame]);

  // Determine the Y-axis domain to give the chart appropriate spacing
  const yDomain = useMemo(() => {
    if (!indexedData || indexedData.length === 0) return [90, 110];
    const values = indexedData.flatMap(item => [item.US, item.CT]);
    const min = Math.min(...values);
    const max = Math.max(...values);
    return [Math.floor(min - 2), Math.ceil(max + 2)];
  }, [indexedData]);

  // If data isn't ready yet, show a loading state
  if (!data || !Array.isArray(data)) {
    return <div>Loading GDP data...</div>;
  }

  return (
    <div>
      <YearSelector 
        timeFrame={timeFrame} 
        setTimeFrame={setTimeFrame} 
        options={[{ value: 1, label: "1 Year" }, { value: 5, label: "5 Years" }]} // 1 year and 5 years options
      />
      <ResponsiveContainer width="100%" height={400}>
        <LineChart data={indexedData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="quarter" />
          <YAxis domain={yDomain} />
          <Tooltip />
          <Legend />
          <Line type="monotone" dataKey="US" stroke="#8884d8" name="US GDP Index" />
          <Line type="monotone" dataKey="CT" stroke="#82ca9d" name="CT GDP Index" />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};